// src/lib/learning/levelAccess.js



import { supabase } from "../../supabaseClient";

import { listUserPracticalChallenges } from "./practicalChallenges";



const LEVEL_PASS_XP = 105;



/**

 * Learning pace:

 *

 * Target:

 * - 3 levels per week

 *

 * Hard limits:

 * - Maximum 1 level per day

 * - Maximum 3 levels per Monday-Sunday week

 *

 * The 3-level value is a learning target, not a minimum

 * access requirement. Learners may continue up to 3.

 */
const MAX_LEVELS_PER_DAY = 1;

const MAX_LEVELS_PER_WEEK = 3;



const REQUIRED_RECENT_TASK_COUNT = 1;



/*

 * Lesson definitions do not change while a learner

 * is navigating the app, so don't download the

 * complete lessons table on every level click.

 */

const LESSON_CACHE_MAX_AGE_MS =

  10 * 60 * 1000;



let lessonsCache = null;

let lessonsCacheSavedAt = 0;

let lessonsRequestPromise = null;



export function clearLevelAccessLessonsCache() {

  lessonsCache = null;

  lessonsCacheSavedAt = 0;

  lessonsRequestPromise = null;

}



async function getLessonsForLevelAccess() {

  const now = Date.now();



  if (

    lessonsCache &&

    now - lessonsCacheSavedAt <

      LESSON_CACHE_MAX_AGE_MS

  ) {

    return lessonsCache;

  }



  /*

   * If two checks start simultaneously,

   * reuse the same request.

   */

  if (lessonsRequestPromise) {

    return lessonsRequestPromise;

  }



  lessonsRequestPromise =

    (async () => {

      const {

        data,

        error,

      } =

        await supabase

          .from("lessons")

          .select(

            "id, quest_id, subquest_id, level_key, title"

          );



      if (error) {

        throw error;

      }



      lessonsCache =

        data || [];



      lessonsCacheSavedAt =

        Date.now();



      return lessonsCache;

    })();



  try {

    return await lessonsRequestPromise;

  } finally {

    lessonsRequestPromise = null;

  }

}



async function resolveUserId(

  suppliedUserId

) {

  /*

   * UserDashboard already knows the user ID.

   *

   * If supplied, ZERO auth request is required.

   */

  if (suppliedUserId) {

    return suppliedUserId;

  }



  /*

   * For direct callers, use the local session

   * instead of auth.getUser().

   */

  const {

    data,

    error,

  } =

    await supabase.auth.getSession();



  if (error) {

    throw error;

  }



  return (

    data?.session?.user?.id ||

    null

  );

}



export const TASKS_ROUTE =

  "/resources?tab=tasks";
const PRACTICAL_ASSIGNMENT_GATES_ENABLED = false;



export const DASHBOARD_ROUTE =

  "/dashboard";



export function normalizeAccessQuestKey(qid) {

  if (!qid) return "";



  if (

    qid === "financial-foundations" ||

    qid === "quest-1" ||

    qid === "Q1"

  ) {

    return "1";

  }



  if (

    qid === "quest-2" ||

    qid === "Q2"

  ) {

    return "2";

  }



  if (

    qid === "quest-3" ||

    qid === "Q3"

  ) {

    return "3";

  }



  return String(qid);

}



export function normalizeAccessLevelKey(

  levelKey

) {

  if (!levelKey) return "";



  const s =

    String(levelKey).trim();



  if (/^L\d+$/i.test(s)) {

    return s.toUpperCase();

  }



  if (/^\d+$/.test(s)) {

    return `L${s}`;

  }



  const match =

    s.match(/(\d+)/);



  return match

    ? `L${match[1]}`

    : s.toUpperCase();

}



export function levelAccessKey(

  questId,

  subquestId,

  levelKey

) {

  return `${normalizeAccessQuestKey(

    questId

  )}|${String(

    subquestId || ""

  )}|${normalizeAccessLevelKey(

    levelKey

  )}`;

}



function parseSubquestNumber(

  subquestId

) {

  const parts =

    String(

      subquestId || ""

    ).split("-");



  const last =

    parts[

      parts.length - 1

    ];



  const n =

    Number.parseInt(

      last,

      10

    );



  return Number.isFinite(n)

    ? n

    : 0;

}



function parseLevelNumber(

  levelKey

) {

  const match =

    String(

      levelKey || ""

    ).match(/(\d+)/);



  const n =

    Number.parseInt(

      match?.[1] || "",

      10

    );



  return Number.isFinite(n)

    ? n

    : 0;

}



function getSortIndex(item) {

  const questNumber =

    Number(

      normalizeAccessQuestKey(

        item.quest_id

      )

    ) || 0;



  const subquestNumber =

    parseSubquestNumber(

      item.subquest_id

    );



  const levelNumber =

    parseLevelNumber(

      item.level_key

    );



  return (

    questNumber * 1000 +

    subquestNumber * 100 +

    levelNumber

  );

}



function isProgressCompleted(

  progressRow

) {

  return (

    !!progressRow?.completed ||

    Number(

      progressRow?.xp_earned ||

        0

    ) >= LEVEL_PASS_XP

  );

}



/**

 * --------------------------------------------------

 * LOCAL DAY RANGE

 * --------------------------------------------------

 *

 * A day runs from local midnight to local midnight.

 */

function getLocalTodayRange() {

  const start =

    new Date();



  start.setHours(

    0,

    0,

    0,

    0

  );



  const end =

    new Date(start);



  end.setDate(

    end.getDate() + 1

  );



  return {

    start,

    end,

  };

}



function isToday(value) {

  if (!value) {

    return false;

  }



  const date =

    new Date(value);



  if (

    Number.isNaN(

      date.getTime()

    )

  ) {

    return false;

  }



  const {

    start,

    end,

  } =

    getLocalTodayRange();



  return (

    date >= start &&

    date < end

  );

}



/**

 * --------------------------------------------------

 * LOCAL WEEK RANGE

 * --------------------------------------------------

 *

 * Gwin's learning week runs:

 *

 * Monday 00:00

 * through

 * next Monday 00:00

 *

 * This uses the learner's local browser timezone.

 */

function getLocalWeekRange() {

  const start =

    new Date();



  start.setHours(

    0,

    0,

    0,

    0

  );



  /*

   * JavaScript:

   * Sunday = 0

   * Monday = 1

   * ...

   * Saturday = 6

   *

   * Convert that into the number of days

   * since Monday.

   */

  const daysSinceMonday =

    (

      start.getDay() +

      6

    ) %

    7;



  start.setDate(

    start.getDate() -

      daysSinceMonday

  );



  const end =

    new Date(start);



  end.setDate(

    end.getDate() + 7

  );



  return {

    start,

    end,

  };

}



function isThisWeek(value) {

  if (!value) {

    return false;

  }



  const date =

    new Date(value);



  if (

    Number.isNaN(

      date.getTime()

    )

  ) {

    return false;

  }



  const {

    start,

    end,

  } =

    getLocalWeekRange();



  return (

    date >= start &&

    date < end

  );

}



function buildTaskMap(

  tasks = []

) {

  const map =

    new Map();



  tasks.forEach(

    (task) => {

      const key =

        levelAccessKey(

          task.quest_id,

          task.subquest_id,

          task.level_key

        );



      if (!key) {

        return;

      }



      map.set(

        key,

        task

      );

    }

  );



  return map;

}



function buildBlockedTaskSummary(

  incompleteTasks = []

) {

  if (

    !incompleteTasks.length

  ) {

    return "";

  }



  const task =

    incompleteTasks[0];



  return task?.title

    ? `Open task: ${task.title}`

    : "You have 1 open practical task.";

}



function sortCompletedLevelsNewestFirst(

  levels = []

) {

  return [

    ...levels,

  ].sort(

    (

      a,

      b

    ) => {

      const aTime =

        new Date(

          a.completed_at ||

            0

        ).getTime();



      const bTime =

        new Date(

          b.completed_at ||

            0

        ).getTime();



      if (

        aTime !== bTime

      ) {

        return (

          bTime -

          aTime

        );

      }



      return (

        b.sortIndex -

        a.sortIndex

      );

    }

  );

}



export async function checkLevelAccessForCurrentUser({

  questId,

  subquestId,

  levelKey,



  /*

   * Pass this from UserDashboard to avoid

   * another auth lookup.

   */

  userId = null,



  /*

   * UserDashboard already knows whether the

   * tapped level has been completed.

   */

  targetAlreadyCompletedHint =

    null,

}) {

  const resolvedUserId =

    await resolveUserId(

      userId

    );



  if (!resolvedUserId) {

    return {

      allowed: false,

      reason:

        "not_authenticated",



      title:

        "Sign in required",



      message:

        "Please sign in before starting a level.",



      buttonLabel:

        "DASHBOARD",



      tasksPath:

        DASHBOARD_ROUTE,

    };

  }



  /*

   * UserDashboard already knows completed levels

   * can be reopened.

   *

   * Skip ALL database checks in that case.

   */

  if (

    targetAlreadyCompletedHint ===

    true

  ) {

    return {

      allowed: true,

      reason: null,



      completedTodayCount:

        null,



      completedThisWeekCount:

        null,



      maxLevelsPerDay:

        MAX_LEVELS_PER_DAY,



      maxLevelsPerWeek:

        MAX_LEVELS_PER_WEEK,



      tasksPath:

        TASKS_ROUTE,

    };

  }



  const targetKey =

    levelAccessKey(

      questId,

      subquestId,

      levelKey

    );



  /*

   * New levels will need the practical-task data,

   * so load progress and tasks simultaneously.

   *

   * Lessons come from the in-memory cache.

   */

  const shouldPrefetchTasks =
  PRACTICAL_ASSIGNMENT_GATES_ENABLED &&
  targetAlreadyCompletedHint ===
    false;



  const [

    lessons,

    progressRes,

    prefetchedTasks,

  ] =

    await Promise.all([

      getLessonsForLevelAccess(),



      supabase

        .from(

          "lesson_progress"

        )

        .select(

          "lesson_id, completed, completed_at, xp_earned"

        )

        .eq(

          "user_id",

          resolvedUserId

        ),



      shouldPrefetchTasks

        ? listUserPracticalChallenges()

        : Promise.resolve(

            null

          ),

    ]);



  if (

    progressRes.error

  ) {

    throw progressRes.error;

  }



  const progressByLessonId =

    new Map(

      (

        progressRes.data ||

        []

      ).map(

        (row) => [

          row.lesson_id,

          row,

        ]

      )

    );



  const completedLevels =

    (

      lessons ||

      []

    )

      .map(

        (lesson) => {

          const progress =

            progressByLessonId.get(

              lesson.id

            );



          if (

            !isProgressCompleted(

              progress

            )

          ) {

            return null;

          }



          const key =

            levelAccessKey(

              lesson.quest_id,

              lesson.subquest_id,

              lesson.level_key

            );



          return {

            ...lesson,



            key,



            completed_at:

              progress

                ?.completed_at ||

              null,



            sortIndex:

              getSortIndex(

                lesson

              ),

          };

        }

      )

      .filter(Boolean);



  const targetAlreadyCompleted =

    completedLevels.some(

      (level) =>

        level.key ===

        targetKey

    );



  const completedTodayCount =

    completedLevels.filter(

      (level) =>

        isToday(

          level.completed_at

        )

    ).length;



  const completedThisWeekCount =

    completedLevels.filter(

      (level) =>

        isThisWeek(

          level.completed_at

        )

    ).length;



  /*

   * Let users reopen/review levels they have

   * already completed regardless of the daily

   * or weekly limit.

   */

  if (

    targetAlreadyCompleted

  ) {

    return {

      allowed: true,

      reason: null,



      completedTodayCount,



      completedThisWeekCount,



      maxLevelsPerDay:

        MAX_LEVELS_PER_DAY,



      maxLevelsPerWeek:

        MAX_LEVELS_PER_WEEK,



      tasksPath:

        TASKS_ROUTE,

    };

  }



  const tasks =
  PRACTICAL_ASSIGNMENT_GATES_ENABLED
    ? prefetchedTasks ||
      (
        await listUserPracticalChallenges()
      )
    : [];



  const taskByLevelKey =

    buildTaskMap(

      tasks

    );



  /*

   * The practical task belonging to the most

   * recently completed level must still be

   * completed before another new level starts.

   */

  const recentCompletedLevels =

    sortCompletedLevelsNewestFirst(

      completedLevels

    ).slice(

      0,

      REQUIRED_RECENT_TASK_COUNT

    );



  const incompleteRecentTasks =

    recentCompletedLevels

      .map(

        (level) =>

          taskByLevelKey.get(

            level.key

          )

      )

      .filter(

        (task) =>

          task &&

          task.status !==

            "completed"

      );



  // TEMPORARY: Localhost-only secure XP testing exemption.
// Remove before committing or deploying.

// Enforce normal daily and weekly learning limits.
const hasReachedDailyLimit =
  completedTodayCount >= MAX_LEVELS_PER_DAY;

const hasReachedWeeklyLimit =
  completedThisWeekCount >= MAX_LEVELS_PER_WEEK;



  /**

   * --------------------------------------------------

   * WEEKLY HARD LIMIT

   * --------------------------------------------------

   *

   * Once 3 levels are completed during the current

   * Monday-Sunday week, no additional new level can

   * start until the next Monday.

   *

   * Completed levels may still be reopened.

   */

  if (

    hasReachedWeeklyLimit

  ) {

   

      



    return {

      allowed: false,



      reason:

        "weekly_limit",



      title: "You've completed your weekly lessons!",

message:
  "Great work! You've completed all 3 levels for this week. " +
  "Your level allowance resets on Monday. " +
  "In the meantime, keep building your financial habits with your weekly check-in.",

buttonLabel: "DASHBOARD",

tasksPath: DASHBOARD_ROUTE,



      incompleteTasks:

        incompleteRecentTasks,



      completedTodayCount,



      completedThisWeekCount,



      maxLevelsPerDay:

        MAX_LEVELS_PER_DAY,



      maxLevelsPerWeek:

        MAX_LEVELS_PER_WEEK,

    };

  }



  /**
 * --------------------------------------------------
 * DAILY HARD LIMIT + OPEN TASK
 * --------------------------------------------------
 *
 * The learner has completed their one new level
 * available today.
 *
 * If the practical task from that level is still
 * open, send them to the task while they wait for
 * tomorrow's new level allowance.
 */
if (
  hasReachedDailyLimit &&
  incompleteRecentTasks.length >
    0
) {
  return {
    allowed: false,

    reason:
      "tasks_required",

    title:
      "Complete your task first",

    message:
      "You’ve completed your level for today. Before another level unlocks tomorrow, complete the practical task from your latest level. " +
      buildBlockedTaskSummary(
        incompleteRecentTasks
      ),

    buttonLabel:
      "TASKS",

    tasksPath:
      TASKS_ROUTE,

    incompleteTasks:
      incompleteRecentTasks,

    completedTodayCount,

    completedThisWeekCount,

    maxLevelsPerDay:
      MAX_LEVELS_PER_DAY,

    maxLevelsPerWeek:
      MAX_LEVELS_PER_WEEK,
  };
}



  /**

   * --------------------------------------------------

   * DAILY HARD LIMIT

   * --------------------------------------------------

   */

  if (

    hasReachedDailyLimit

  ) {

    return {

      allowed: false,



      reason:

        "daily_limit",



      title:

        "You’re done for today",



      message:

        "Great work — you’ve completed your 1 level for today. Come back tomorrow to continue your journey.",



      buttonLabel:

        "DASHBOARD",



      tasksPath:

        DASHBOARD_ROUTE,



      completedTodayCount,



      completedThisWeekCount,



      maxLevelsPerDay:

        MAX_LEVELS_PER_DAY,



      maxLevelsPerWeek:

        MAX_LEVELS_PER_WEEK,

    };

  }



  /**

   * --------------------------------------------------

   * PRACTICAL TASK GATE

   * --------------------------------------------------

   *

   * Example:

   *

   * Level 1 completed

   * → Task 1 must be completed

   * → then Level 2 may start

   *

   * This remains true even though learners may now

   * complete up to 1 level in one day.

   */

  if (

    incompleteRecentTasks.length >

    0

  ) {

    return {

      allowed: false,



      reason:

        "tasks_required",



      title:

        "Complete your task first",



      message:

        "Before starting another level, complete the practical task from your most recent completed level. " +

        buildBlockedTaskSummary(

          incompleteRecentTasks

        ),



      buttonLabel:

        "TASKS",



      tasksPath:

        TASKS_ROUTE,



      incompleteTasks:

        incompleteRecentTasks,



      completedTodayCount,



      completedThisWeekCount,



      maxLevelsPerDay:

        MAX_LEVELS_PER_DAY,



      maxLevelsPerWeek:

        MAX_LEVELS_PER_WEEK,

    };

  }



  /**

   * --------------------------------------------------

   * ACCESS ALLOWED

   * --------------------------------------------------

   */

  return {

    allowed: true,

    reason: null,



    completedTodayCount,



    completedThisWeekCount,



    maxLevelsPerDay:

      MAX_LEVELS_PER_DAY,



    maxLevelsPerWeek:

      MAX_LEVELS_PER_WEEK,



    tasksPath:

      TASKS_ROUTE,

  };

}